interface RequestSession {
  sessionGeneration: number;
  userId: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  setAccessToken(token: string, expectedGeneration: number): boolean;
  logout(): void;
}

export class AuthSessionChangedError extends Error {
  constructor() {
    super('The signed-in account changed. Please try again.');
    this.name = 'AuthSessionChangedError';
  }
}

export class SessionExpiredError extends Error {
  constructor() {
    super('Session expired. Please sign in again.');
    this.name = 'SessionExpiredError';
  }
}

export class ApiResponseError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiResponseError';
  }
}

/** Bind every response, refresh and retry to the session that sent the request. */
export function createApiRequester(deps: {
  baseUrl: string;
  getSession(): RequestSession;
  fetch: typeof fetch;
}) {
  const refreshes = new Map<number, Promise<boolean>>();

  function assertCurrent(session: RequestSession) {
    const current = deps.getSession();
    if (current.sessionGeneration !== session.sessionGeneration || current.userId !== session.userId) {
      throw new AuthSessionChangedError();
    }
  }

  async function refresh(session: RequestSession): Promise<boolean> {
    assertCurrent(session);
    if (!session.refreshToken) return false;
    const existing = refreshes.get(session.sessionGeneration);
    if (existing) return existing;

    const pending = (async () => {
      const res = await deps.fetch(`${deps.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: session.refreshToken }),
      });
      assertCurrent(session);
      if ([400, 401, 403].includes(res.status)) return false;
      if (!res.ok) throw new Error('Session refresh is unavailable. Please try again.');
      const data: unknown = await res.json();
      assertCurrent(session);
      if (!data || typeof data !== 'object' || !('accessToken' in data)
        || typeof data.accessToken !== 'string' || !data.accessToken.trim()) {
        throw new Error('Session refresh returned an invalid response. Please try again.');
      }
      if (!deps.getSession().setAccessToken(data.accessToken, session.sessionGeneration)) {
        throw new AuthSessionChangedError();
      }
      return true;
    })();
    refreshes.set(session.sessionGeneration, pending);
    try {
      return await pending;
    } finally {
      if (refreshes.get(session.sessionGeneration) === pending) refreshes.delete(session.sessionGeneration);
    }
  }

  async function withinSession<T>(
    path: string, options: RequestInit | undefined, retry: boolean, session: RequestSession,
  ): Promise<T> {
    assertCurrent(session);
    options?.signal?.throwIfAborted();
    const tokenSent = deps.getSession().accessToken;
    const headers = new Headers(options?.headers);
    if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    if (tokenSent) headers.set('Authorization', `Bearer ${tokenSent}`);
    else headers.delete('Authorization');
    const res = await deps.fetch(`${deps.baseUrl}${path}`, { ...options, headers });
    assertCurrent(session);
    options?.signal?.throwIfAborted();

    if (res.status === 401 && retry && tokenSent) {
      // Another request may already have refreshed this session while this
      // response was in flight. Its late 401 does not require a second refresh.
      const refreshed = deps.getSession().accessToken !== tokenSent || await refresh(session);
      assertCurrent(session);
      options?.signal?.throwIfAborted();
      if (refreshed) return withinSession<T>(path, options, false, session);
      deps.getSession().logout();
      throw new SessionExpiredError();
    }

    if (!res.ok) {
      const data: unknown = await res.json().catch(() => null);
      assertCurrent(session);
      const message = data && typeof data === 'object' && 'error' in data && typeof data.error === 'string'
        ? data.error : `HTTP ${res.status}`;
      const code = data && typeof data === 'object' && 'code' in data && typeof data.code === 'string'
        ? data.code : undefined;
      throw new ApiResponseError(res.status, message, code);
    }
    const data: T = await res.json();
    assertCurrent(session);
    options?.signal?.throwIfAborted();
    return data;
  }

  return async function request<T>(path: string, options?: RequestInit, retry = true): Promise<T> {
    const session = deps.getSession();
    try {
      return await withinSession<T>(path, options, retry, session);
    } catch (error) {
      // A logout performed above deliberately invalidates this session.
      if (error instanceof SessionExpiredError) throw error;
      assertCurrent(session);
      if (error instanceof TypeError) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      throw error;
    }
  };
}
